import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  HelpCircle, X, Plus, Search, Loader2, Pencil, Trash2, Eye, EyeOff, ArrowUp, ArrowDown,
  AlertCircle, RefreshCw, Save, ArrowLeft
} from 'lucide-react';
import { FaqItem, FaqResposta, iconeDaCategoria } from './FaqPanel';
import { avisar, confirmar } from './Feedback';

interface FaqAdminProps {
  supabaseClient: any;
  currentUserId?: string;
  onClose: () => void;
  onChange: () => void; // avisa o App para recarregar itens/configuração
}

type Rascunho = { id: string | null; categoria: string; pergunta: string; resposta: string; ativo: boolean };

const RASCUNHO_VAZIO: Rascunho = { id: null, categoria: '', pergunta: '', resposta: '', ativo: true };
const CHAVE_PAINEL = 'faq_painel_ativo';

export const FaqAdmin: React.FC<FaqAdminProps> = ({ supabaseClient, currentUserId, onClose, onChange }) => {
  const [itens, setItens] = useState<FaqItem[]>([]);
  const [painelAtivo, setPainelAtivo] = useState(true);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [rascunho, setRascunho] = useState<Rascunho | null>(null);
  const [erroRascunho, setErroRascunho] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [salvandoPainel, setSalvandoPainel] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const carregar = async () => {
    setCarregando(true);
    setErro('');
    try {
      const [itensRes, configRes] = await Promise.all([
        supabaseClient.from('faq_itens').select('id, categoria, pergunta, resposta, ordem, ativo').order('ordem', { ascending: true }),
        supabaseClient.from('app_settings').select('value').eq('key', CHAVE_PAINEL).maybeSingle()
      ]);
      if (itensRes.error) throw itensRes.error;
      setItens(itensRes.data || []);
      setPainelAtivo(configRes.data ? configRes.data.value !== 'false' : true);
    } catch (e: any) {
      const msg = e?.message || String(e);
      setErro(/faq_itens|relation|schema cache/i.test(msg)
        ? 'A tabela da Central de Dúvidas ainda não existe. Execute o script sql/create-faq.sql no SQL Editor do Supabase e clique em "Tentar de novo".'
        : msg);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  // Esc fecha o editor primeiro, depois o modal
  const rascunhoAberto = rascunho !== null;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      if (rascunhoAberto) setRascunho(null); else onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [rascunhoAberto]);

  const categoriasExistentes = useMemo(
    () => Array.from(new Set(itens.map(i => i.categoria))),
    [itens]
  );

  const itensFiltrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return itens;
    return itens.filter(i =>
      i.pergunta.toLowerCase().includes(q) || i.resposta.toLowerCase().includes(q) || i.categoria.toLowerCase().includes(q));
  }, [itens, busca]);

  const alternarPainel = async () => {
    const novo = !painelAtivo;
    setSalvandoPainel(true);
    try {
      const { error } = await supabaseClient.from('app_settings')
        .upsert({ key: CHAVE_PAINEL, value: novo ? 'true' : 'false', updated_at: new Date().toISOString() }, { onConflict: 'key' });
      if (error) throw error;
      setPainelAtivo(novo);
      onChange();
    } catch (e: any) {
      avisar(`Não foi possível alterar a configuração: ${e?.message || e}`);
    } finally {
      setSalvandoPainel(false);
    }
  };

  const salvarRascunho = async () => {
    if (!rascunho) return;
    const categoria = rascunho.categoria.trim();
    const pergunta = rascunho.pergunta.trim();
    const resposta = rascunho.resposta.trim();
    if (!categoria || !pergunta || !resposta) {
      setErroRascunho('Preencha o tema, a pergunta e a resposta.');
      return;
    }
    setSalvando(true);
    setErroRascunho('');
    try {
      const agora = new Date().toISOString();
      if (rascunho.id) {
        const { error } = await supabaseClient.from('faq_itens')
          .update({ categoria, pergunta, resposta, ativo: rascunho.ativo, atualizado_em: agora, atualizado_por: currentUserId || null })
          .eq('id', rascunho.id);
        if (error) throw error;
      } else {
        // Nova pergunta entra no fim do seu tema (ou no fim da lista, se o tema for novo)
        const doTema = itens.filter(i => i.categoria === categoria);
        const base = doTema.length ? Math.max(...doTema.map(i => i.ordem)) : Math.max(0, ...itens.map(i => i.ordem));
        const { error } = await supabaseClient.from('faq_itens')
          .insert({ categoria, pergunta, resposta, ativo: rascunho.ativo, ordem: base + 5, atualizado_em: agora, atualizado_por: currentUserId || null });
        if (error) throw error;
      }
      setRascunho(null);
      await carregar();
      onChange();
    } catch (e: any) {
      setErroRascunho(`Erro ao salvar: ${e?.message || e}`);
    } finally {
      setSalvando(false);
    }
  };

  const alternarAtivo = async (item: FaqItem) => {
    const { error } = await supabaseClient.from('faq_itens').update({ ativo: !item.ativo }).eq('id', item.id);
    if (error) { avisar(`Erro: ${error.message}`); return; }
    setItens(prev => prev.map(i => i.id === item.id ? { ...i, ativo: !i.ativo } : i));
    onChange();
  };

  const excluir = async (item: FaqItem) => {
    if (!(await confirmar(`Excluir a pergunta "${item.pergunta}"?\n\nSe quiser apenas escondê-la dos usuários, use o botão de ocultar.`, { titulo: 'Excluir pergunta', rotuloConfirmar: 'Excluir' }))) return;
    const { error } = await supabaseClient.from('faq_itens').delete().eq('id', item.id);
    if (error) { avisar(`Erro ao excluir: ${error.message}`); return; }
    setItens(prev => prev.filter(i => i.id !== item.id));
    onChange();
  };

  // Troca a posição com o vizinho e renumera a lista (10, 20, 30...)
  const mover = async (item: FaqItem, direcao: -1 | 1) => {
    const idx = itens.findIndex(i => i.id === item.id);
    const alvo = idx + direcao;
    if (idx < 0 || alvo < 0 || alvo >= itens.length) return;
    const nova = [...itens];
    [nova[idx], nova[alvo]] = [nova[alvo], nova[idx]];
    const renumerada = nova.map((i, n) => ({ ...i, ordem: (n + 1) * 10 }));
    const alterados = renumerada.filter((i, n) => i.ordem !== itens.find(o => o.id === i.id)?.ordem || n === idx || n === alvo);
    setItens(renumerada);
    const { error } = await supabaseClient.from('faq_itens').upsert(alterados, { onConflict: 'id' });
    if (error) { avisar(`Erro ao reordenar: ${error.message}`); carregar(); return; }
    onChange();
  };

  const totalAtivos = itens.filter(i => i.ativo).length;

  return (
    <div className="fixed inset-0 z-[116] bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4 modal-backdrop">
      <div role="dialog" aria-modal="true" aria-labelledby="faq-admin-titulo"
        className="bg-white w-full sm:max-w-5xl h-full sm:h-auto sm:max-h-[92vh] sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden modal-content">

        {/* Cabeçalho */}
        <div className="bg-[#08162D] px-5 sm:px-7 py-5 border-b-2 border-[#D92B2B] flex items-center justify-between gap-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            {rascunho ? (
              <button onClick={() => setRascunho(null)} className="w-11 h-11 -ml-2 flex items-center justify-center rounded-lg text-[#B8C1D1] hover:text-white hover:bg-white/10" aria-label="Voltar para a lista">
                <ArrowLeft className="w-5 h-5" />
              </button>
            ) : (
              <div className="w-11 h-11 rounded-xl bg-[#D92B2B] flex items-center justify-center" aria-hidden="true">
                <HelpCircle className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <h2 id="faq-admin-titulo" className="text-lg sm:text-xl font-bold text-white leading-tight">
                {rascunho ? (rascunho.id ? 'Editar pergunta' : 'Nova pergunta') : 'Gerenciar Central de Dúvidas'}
              </h2>
              <p className="text-sm text-[#B8C1D1] mt-0.5">
                {rascunho ? 'A prévia mostra como o usuário vai ler a resposta' : `${itens.length} perguntas · ${totalAtivos} visíveis para os usuários`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-11 h-11 flex items-center justify-center rounded-lg text-[#B8C1D1] hover:text-white hover:bg-white/10" aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        </div>

        {carregando ? (
          <div className="flex-1 flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-slate-400 animate-spin" aria-label="Carregando" />
          </div>
        ) : erro ? (
          <div className="p-7">
            <div className="flex gap-3 rounded-2xl border border-red-200 bg-red-50 p-5">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div className="space-y-3">
                <p className="text-sm text-red-900">{erro}</p>
                <button onClick={carregar} className="h-10 px-4 rounded-lg bg-white border border-red-300 text-sm font-semibold text-red-800 hover:bg-red-100 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" aria-hidden="true" /> Tentar de novo
                </button>
              </div>
            </div>
          </div>
        ) : rascunho ? (
          /* ── Editor ── */
          <>
            <div className="flex-1 overflow-y-auto">
              <div className="grid lg:grid-cols-2">
                <div className="p-5 sm:p-7 space-y-5 lg:border-r border-slate-200">
                  <div>
                    <label htmlFor="faq-categoria" className="block text-sm font-semibold text-slate-900 mb-1.5">Tema</label>
                    <input id="faq-categoria" list="faq-categorias" value={rascunho.categoria}
                      onChange={(e) => setRascunho({ ...rascunho, categoria: e.target.value })}
                      placeholder="Ex.: Prestação de contas"
                      className="w-full h-12 px-4 rounded-xl border border-slate-300 focus:border-[#0B3B8C] focus:ring-2 focus:ring-[#0B3B8C]/20 focus:outline-none text-[15px]" />
                    <datalist id="faq-categorias">
                      {categoriasExistentes.map(c => <option key={c} value={c} />)}
                    </datalist>
                    <p className="text-xs text-slate-500 mt-1.5">Escolha um tema existente ou digite um novo.</p>
                  </div>
                  <div>
                    <label htmlFor="faq-pergunta" className="block text-sm font-semibold text-slate-900 mb-1.5">Pergunta</label>
                    <input id="faq-pergunta" value={rascunho.pergunta}
                      onChange={(e) => setRascunho({ ...rascunho, pergunta: e.target.value })}
                      placeholder="Escreva como o usuário perguntaria"
                      className="w-full h-12 px-4 rounded-xl border border-slate-300 focus:border-[#0B3B8C] focus:ring-2 focus:ring-[#0B3B8C]/20 focus:outline-none text-[15px]" />
                  </div>
                  <div>
                    <label htmlFor="faq-resposta" className="block text-sm font-semibold text-slate-900 mb-1.5">Resposta</label>
                    <textarea id="faq-resposta" value={rascunho.resposta} rows={12}
                      onChange={(e) => setRascunho({ ...rascunho, resposta: e.target.value })}
                      placeholder="Explique em frases curtas, como se fosse a primeira vez da pessoa."
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-[#0B3B8C] focus:ring-2 focus:ring-[#0B3B8C]/20 focus:outline-none text-[15px] leading-relaxed resize-y" />
                    <div className="mt-2 rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-1">
                      <p className="font-semibold text-slate-700">Como formatar</p>
                      <p><code className="bg-white border border-slate-200 rounded px-1">- item</code> lista com marcadores</p>
                      <p><code className="bg-white border border-slate-200 rounded px-1">1. passo</code> passo a passo numerado</p>
                      <p><code className="bg-white border border-slate-200 rounded px-1">**texto**</code> negrito · linha em branco separa parágrafos · e-mails viram links</p>
                    </div>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer min-h-[44px]">
                    <input type="checkbox" checked={rascunho.ativo} onChange={(e) => setRascunho({ ...rascunho, ativo: e.target.checked })}
                      className="w-5 h-5 accent-[#08162D]" />
                    <span className="text-sm text-slate-800">Visível para os usuários</span>
                  </label>
                  {erroRascunho && (
                    <p className="text-sm text-red-700 font-semibold flex items-center gap-1.5" role="alert">
                      <AlertCircle className="w-4 h-4" aria-hidden="true" />{erroRascunho}
                    </p>
                  )}
                </div>

                {/* Prévia */}
                <div className="p-5 sm:p-7 bg-slate-50">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Prévia</p>
                  <div className="rounded-2xl border border-slate-300 bg-white shadow-sm">
                    <div className="px-5 py-3.5 text-[15px] font-semibold text-slate-900">
                      {rascunho.pergunta || <span className="text-slate-400 font-normal">A pergunta aparece aqui</span>}
                    </div>
                    <div className="px-5 pb-5">
                      <div className="border-l-2 border-[#D92B2B]/40 pl-4">
                        {rascunho.resposta.trim()
                          ? <FaqResposta texto={rascunho.resposta} />
                          : <p className="text-sm text-slate-400">A resposta aparece aqui</p>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 border-t border-slate-200 px-5 sm:px-7 py-4 flex justify-end gap-2">
              <button onClick={() => setRascunho(null)} className="h-11 px-5 rounded-xl border border-slate-300 text-sm font-semibold text-slate-800 hover:bg-slate-50">Cancelar</button>
              <button onClick={salvarRascunho} disabled={salvando}
                className="h-11 px-6 rounded-xl bg-[#08162D] hover:bg-[#13284D] text-white text-sm font-bold flex items-center gap-2 disabled:opacity-50">
                {salvando ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Save className="w-4 h-4" aria-hidden="true" />}
                {salvando ? 'Salvando...' : 'Salvar pergunta'}
              </button>
            </div>
          </>
        ) : (
          /* ── Lista ── */
          <div className="flex-1 overflow-y-auto">
            {/* Liga/desliga */}
            <div className="px-5 sm:px-7 pt-5">
              <div className={`rounded-2xl border p-4 sm:p-5 flex items-start sm:items-center justify-between gap-4 ${painelAtivo ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                <div>
                  <p id="faq-painel-label" className="font-semibold text-slate-900">
                    Painel {painelAtivo ? 'ligado' : 'desligado'}
                  </p>
                  <p className="text-sm text-slate-600 mt-0.5">
                    {painelAtivo
                      ? 'Abre automaticamente para os usuários a cada entrada no sistema e fica disponível no ícone ? do topo.'
                      : 'Os usuários não veem o painel. Você continua podendo editar as perguntas.'}
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={painelAtivo}
                  aria-labelledby="faq-painel-label"
                  onClick={alternarPainel}
                  disabled={salvandoPainel}
                  className={`relative flex-shrink-0 w-14 h-8 rounded-full transition-colors disabled:opacity-60 ${painelAtivo ? 'bg-emerald-600' : 'bg-slate-400'}`}
                >
                  <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${painelAtivo ? 'translate-x-6' : ''}`} />
                </button>
              </div>
            </div>

            {/* Barra */}
            <div className="px-5 sm:px-7 pt-5 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" aria-hidden="true" />
                <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar pergunta, resposta ou tema"
                  aria-label="Buscar perguntas"
                  className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-300 focus:border-[#0B3B8C] focus:ring-2 focus:ring-[#0B3B8C]/20 focus:outline-none text-sm" />
              </div>
              <button onClick={() => { setRascunho({ ...RASCUNHO_VAZIO }); setErroRascunho(''); }}
                className="h-11 px-5 rounded-xl bg-[#D92B2B] hover:bg-[#B71F1F] text-white text-sm font-bold flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" aria-hidden="true" /> Nova pergunta
              </button>
            </div>

            {/* Itens */}
            <div className="px-5 sm:px-7 py-5">
              {itensFiltrados.length === 0 ? (
                <p className="text-center text-sm text-slate-500 py-12">
                  {itens.length === 0 ? 'Nenhuma pergunta cadastrada. Clique em "Nova pergunta" para começar.' : 'Nenhuma pergunta encontrada.'}
                </p>
              ) : (
                <ul className="divide-y divide-slate-200 border border-slate-200 rounded-2xl overflow-hidden">
                  {itensFiltrados.map(item => {
                    const Icone = iconeDaCategoria(item.categoria);
                    const pos = itens.findIndex(i => i.id === item.id);
                    return (
                      <li key={item.id} className={`flex items-center gap-3 px-4 py-3 ${item.ativo ? 'bg-white' : 'bg-slate-50'}`}>
                        <div className="flex flex-col">
                          <button onClick={() => mover(item, -1)} disabled={pos === 0 || !!busca}
                            className="w-8 h-7 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
                            aria-label={`Subir "${item.pergunta}"`}><ArrowUp className="w-4 h-4" /></button>
                          <button onClick={() => mover(item, 1)} disabled={pos === itens.length - 1 || !!busca}
                            className="w-8 h-7 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
                            aria-label={`Descer "${item.pergunta}"`}><ArrowDown className="w-4 h-4" /></button>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-[15px] font-semibold leading-snug ${item.ativo ? 'text-slate-900' : 'text-slate-500'}`}>{item.pergunta}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                              <Icone className="w-3.5 h-3.5 text-[#D92B2B]" aria-hidden="true" />{item.categoria}
                            </span>
                            {!item.ativo && <span className="text-[11px] font-bold text-slate-600 bg-slate-200 rounded px-1.5 py-0.5">oculta</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={() => alternarAtivo(item)}
                            className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                            aria-label={item.ativo ? 'Ocultar dos usuários' : 'Mostrar aos usuários'} title={item.ativo ? 'Ocultar dos usuários' : 'Mostrar aos usuários'}>
                            {item.ativo ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                          <button onClick={() => { setRascunho({ id: item.id, categoria: item.categoria, pergunta: item.pergunta, resposta: item.resposta, ativo: item.ativo }); setErroRascunho(''); }}
                            className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                            aria-label="Editar" title="Editar"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => excluir(item)}
                            className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-500 hover:text-red-700 hover:bg-red-50"
                            aria-label="Excluir" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {busca && <p className="text-xs text-slate-500 mt-3">Para reordenar, limpe a busca.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
